import { toast } from 'sonner';
import { emptyTypeBreakdown, ISSUE_COPY_ITEMS, ISSUE_TYPE_VALUES, issueCopyLabel } from '@overlayqa/shared';
import type {
  TicketSeverity,
  TicketStatus,
  DashboardStats,
  SeverityBreakdown,
  StatusBreakdown,
  TypeBreakdown,
  TypeBreakdownByRange,
  IssueAgingData,
  DashboardActivityItem,
  DashboardNeedsAttentionIssue,
} from '@overlayqa/shared';
import { SeverityBadge } from '../../components/shared/badges/SeverityBadge';
import { StatusBadge } from '../../components/shared/badges/StatusBadge';
import type { ColumnDef, RowAction, BulkAction } from '../../components/shared/DataTableTypes';
import type { ProjectWithPreview } from '../../lib/api/projects';

/**
 * Static fixtures for the /design-system showcase.
 *
 * Extracted from DesignSystemPage.tsx on 2026-08-07 (900-line ceiling).
 * Structure only: every value is byte-identical to its inline original.
 *
 * Everything here is self-contained by design. The preview images are SVG data
 * URIs rather than network assets so the gallery renders identically offline
 * and under jsdom, where the appComponents suite asserts against their alt text.
 */

// ---- data -------------------------------------------------------------------

export const SEVERITIES: TicketSeverity[] = ['critical', 'high', 'medium', 'low'];
export const STATUSES: TicketStatus[] = ['open', 'in-progress', 'resolved', 'verified', 'closed'];
// Derived from the shared vocabulary, never authored: the four-entry literal
// this was until 2026-09-23 left Improvement, General and Token off the
// showcase, so the design system was not the source of truth for three of
// seven type badges (found while verifying General's brand-blue repaint).
export const TYPES = ISSUE_TYPE_VALUES;
export const UI_BUTTON_VARIANTS = ['primary', 'secondary', 'outline', 'ghost', 'link'] as const;

// Avatar image variant demo: self-contained data-URI (no network).
export const AVATAR_DATA_URI =
  'data:image/svg+xml,' +
  encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" fill="#3468F8"/><circle cx="16" cy="12" r="5" fill="#fff"/><ellipse cx="16" cy="25" rx="9" ry="6" fill="#fff"/></svg>');

export const SWATCH_GROUPS: { label: string; items: { token: string; value: string }[] }[] = [
  { label: 'Brand', items: [
    { token: '--color-primary (500)', value: 'var(--color-primary)' },
    { token: '--color-primary-hover (600)', value: 'var(--color-primary-hover)' },
    { token: '--color-primary-light (50)', value: 'var(--color-primary-light)' },
    { token: '--color-surface (auth card)', value: 'var(--color-surface)' },
  ] },
  { label: 'Surface (app ground)', items: [
    { token: '--color-bg (Neural White ground)', value: 'var(--color-bg)' },
    { token: '--color-card-stroke (card border)', value: 'var(--color-card-stroke)' },
  ] },
  { label: 'Severity', items: SEVERITIES.map((s) => ({ token: `--color-severity-${s}`, value: `var(--color-severity-${s})` })) },
  { label: 'Issue types', items: [
    { token: '--color-type-bug', value: 'var(--color-type-bug)' },
    { token: '--color-type-gap', value: 'var(--color-type-gap)' },
    { token: '--color-type-debt', value: 'var(--color-type-debt)' },
    { token: '--color-type-a11y', value: 'var(--color-type-a11y)' },
  ] },
  { label: 'Semantic', items: [
    { token: '--color-error', value: 'var(--color-error)' },
    { token: '--color-warning', value: 'var(--color-warning)' },
    { token: '--color-success', value: 'var(--color-success)' },
  ] },
  // Directional trend, added 2026-08-10 with the delta chip. Listed as its own
  // group rather than folded into Semantic because the pair is a third role:
  // "which way did a number move", not "an operation succeeded" and not a chart
  // series. The -bg halves are here too, since a text token whose tint is not
  // shown beside it is how a 3.58:1 pair went unnoticed in Semantic for months.
  { label: 'Directional trend', items: [
    { token: '--color-trend-up-text', value: 'var(--color-trend-up-text)' },
    { token: '--color-trend-up-bg', value: 'var(--color-trend-up-bg)' },
    { token: '--color-trend-down-text', value: 'var(--color-trend-down-text)' },
    { token: '--color-trend-down-bg', value: 'var(--color-trend-down-bg)' },
  ] },
  { label: 'Provider brand', items: [
    { token: '--color-jira', value: 'var(--color-jira)' },
    { token: '--color-linear', value: 'var(--color-linear)' },
    { token: '--color-notion', value: 'var(--color-notion)' },
    { token: '--color-asana', value: 'var(--color-asana)' },
    { token: '--color-trello', value: 'var(--color-trello)' },
    { token: '--color-claude', value: 'var(--color-claude)' },
    { token: '--color-cursor', value: 'var(--color-cursor)' },
    { token: '--color-zapier', value: 'var(--color-zapier)' },
    { token: '--color-make', value: 'var(--color-make)' },
    { token: '--color-framer', value: 'var(--color-framer)' },
  ] },
];

export type DemoRow = { id: string; name: string; severity: TicketSeverity; status: TicketStatus };
export const DEMO_ROWS: DemoRow[] = [
  { id: '1', name: 'Button contrast below AA', severity: 'critical', status: 'open' },
  { id: '2', name: 'Heading skips a level', severity: 'medium', status: 'in-progress' },
  { id: '3', name: 'Icon misaligned in flex row', severity: 'low', status: 'resolved' },
];
export const DEMO_COLUMNS: ColumnDef<DemoRow>[] = [
  // Cell typography mirrors the real Issues table (issuesPageColumns.tsx): text-xs.
  // NOTE: ProjectsPage cells currently run text-sm — known divergence.
  { header: 'Issue', render: (r) => <span className="text-xs font-medium text-gray-900">{r.name}</span> },
  { header: 'Severity', render: (r) => <SeverityBadge severity={r.severity} /> },
  { header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
];
export const DEMO_ROW_ACTIONS: RowAction<DemoRow>[] = [
  { label: 'Edit', onClick: () => toast('Edit (demo)') },
  // Submenus open on hover as a leftward flyout. Click and ArrowRight open them
  // too, so keyboard users are never stranded.
  {
    label: 'Change status',
    onClick: () => {},
    submenu: [
      { label: 'Open', checked: true, onClick: () => toast('Status: Open (demo)') },
      { label: 'Resolved', onClick: () => toast('Status: Resolved (demo)') },
    ],
  },
  {
    label: 'Export',
    onClick: () => {},
    submenu: [
      { label: 'Export to Jira', disabled: true, title: 'Connect Jira in Integrations to export issues.', onClick: () => {} },
      { label: 'Export to Linear', onClick: () => toast('Export to Linear (demo)') },
      { label: 'Export to Notion', disabled: true, title: 'Connect Notion in Integrations to export issues.', onClick: () => {} },
      { label: 'Export to Asana', onClick: () => toast('Export to Asana (demo)') },
      { label: 'Export to Trello', disabled: true, title: 'Connect Trello in Integrations to export issues.', onClick: () => {} },
    ],
  },
  // Copy for saved issues (2026-10-25): the Issues page's Copy category after
  // Export, labels from the shared issueCopyLabel (never retyped).
  {
    label: 'Copy',
    onClick: () => {},
    submenu: ISSUE_COPY_ITEMS.map((format) => ({ label: issueCopyLabel(format, 1), onClick: () => toast(`${issueCopyLabel(format, 1)} (demo)`) })),
  },
  { label: 'Archive', onClick: () => {}, disabled: () => true, disabledTitle: 'Only the project owner can do this' },
  { label: 'Delete', destructive: true, onClick: () => toast('Delete (demo)') },
];
export const DEMO_BULK_ACTIONS: BulkAction<DemoRow>[] = [
  { label: 'Export', onClick: async () => {}, submenu: [{ label: 'Linear', onClick: () => toast('Export to Linear (demo)') }, { label: 'Jira', onClick: () => toast('Export to Jira (demo)') }] },
  { label: 'Copy', onClick: async () => {}, clearSelectionOnSuccess: false, getSubmenu: (ids) => ISSUE_COPY_ITEMS.map((format) => ({ label: issueCopyLabel(format, ids.length), onClick: () => toast(`${issueCopyLabel(format, ids.length)} (demo)`) })) },
  { label: 'Delete', destructive: true, onClick: async () => { toast('Bulk delete (demo)'); } },
];

// ---- app-card demo data -------------------------------------------------

/** Self-contained SVG page mock (no network) for thumbnail/screenshot demos. */
function pagePreviewDataUri(accent: string): string {
  return (
    'data:image/svg+xml,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 400"><rect width="600" height="400" fill="#f8fafc"/><rect width="600" height="48" fill="${accent}"/><circle cx="24" cy="24" r="8" fill="#ffffff" opacity="0.9"/><rect x="48" y="16" width="120" height="16" rx="8" fill="#ffffff" opacity="0.7"/><rect x="32" y="80" width="536" height="112" rx="12" fill="#e2e8f0"/><rect x="32" y="216" width="256" height="152" rx="12" fill="#e2e8f0"/><rect x="312" y="216" width="256" height="152" rx="12" fill="#e2e8f0"/></svg>`
    )
  );
}
export const PROJECT_PREVIEW_URI = pagePreviewDataUri('#3468F8');
export const SCREENSHOT_DEMO_URI = pagePreviewDataUri('#0f172a');

const NOW_ISO = new Date().toISOString();
export const noop = () => {};

export const DEMO_PROJECT: ProjectWithPreview = {
  id: 'ds-project-1',
  teamId: 'ds-team',
  name: 'Acme Storefront',
  slug: 'acme-storefront',
  primaryDomain: 'acme.com',
  createdAt: NOW_ISO,
  updatedAt: NOW_ISO,
  previewUrl: PROJECT_PREVIEW_URI,
  ticketCount: 12,
  role: 'owner',
  members: [
    { id: 'ds-user-1', name: 'Maya Chen', avatarUrl: null },
    { id: 'ds-user-2', name: 'Jordan Lee', avatarUrl: null },
    { id: 'ds-user-3', name: 'Priya Shah', avatarUrl: null },
    { id: 'ds-user-4', name: 'Sam Ortiz', avatarUrl: null },
    { id: 'ds-user-5', name: 'Alex Kim', avatarUrl: null },
  ],
};

/** The dashboard's My Projects renders ProjectCard from the Projects page list, so the fixture is that shape. */
export const DEMO_RECENT_PROJECTS: ProjectWithPreview[] = [
  { ...DEMO_PROJECT, id: 'ds-recent-1', name: 'Aurora Dashboard', slug: 'aurora-dashboard', primaryDomain: 'aurora.app', ticketCount: 12, members: DEMO_PROJECT.members?.slice(0, 2) },
  { ...DEMO_PROJECT, id: 'ds-recent-2', name: 'Sample Store', slug: 'sample-store', previewUrl: null, primaryDomain: null, ticketCount: 4, role: 'team', members: DEMO_PROJECT.members?.slice(0, 1) },
];

export const DEMO_STATS: DashboardStats = { total: 128, open: 34, closed: 86, thisWeek: 12, lastWeek: 9, thisMonth: 41, lastMonth: 30 };
export const DEMO_SEVERITY_BREAKDOWN: SeverityBreakdown = { critical: 3, high: 9, medium: 14, low: 8 };
export const DEMO_STATUS_BREAKDOWN: StatusBreakdown = { open: 34, 'in-progress': 11, resolved: 22, verified: 9, closed: 52 };
export const DEMO_TYPE_BREAKDOWN: TypeBreakdown = { ...emptyTypeBreakdown(), 'design-bug': 16, 'design-gap': 7, 'design-debt': 5, accessibility: 6 };
export const DEMO_TYPES_BY_RANGE: TypeBreakdownByRange = {
  week: { ...emptyTypeBreakdown(), 'design-bug': 4, 'design-gap': 2, 'design-debt': 1, accessibility: 2 },
  month: DEMO_TYPE_BREAKDOWN,
  quarter: { ...emptyTypeBreakdown(), 'design-bug': 31, 'design-gap': 15, 'design-debt': 12, accessibility: 14 },
};
export const DEMO_ISSUE_AGING: IssueAgingData = { lessThan1d: 4, from1to3d: 7, from3to7d: 9, from7to14d: 5, over14d: 3 };

function hoursAgoIso(hours: number): string {
  return new Date(Date.now() - hours * 3_600_000).toISOString();
}
export const DEMO_ACTIVITY: DashboardActivityItem[] = [
  { id: 'ds-act-1', displayId: 'OV2-127', type: 'issue_created', title: 'Button contrast below AA', projectName: 'Acme Storefront', timestamp: hoursAgoIso(2), severity: 'critical' },
  { id: 'ds-act-2', displayId: 'OV2-121', type: 'status_in_progress', title: 'Heading skips a level', projectName: 'Acme Storefront', timestamp: hoursAgoIso(5), severity: 'medium' },
  { id: 'ds-act-3', displayId: 'OV2-118', type: 'status_resolved', title: 'Icon misaligned in flex row', projectName: 'Marketing Site', timestamp: hoursAgoIso(26), severity: 'low' },
];
export const DEMO_NEEDS_ATTENTION: DashboardNeedsAttentionIssue[] = [
  { id: 'ds-att-1', displayId: 'OV2-127', title: 'Button contrast below AA', projectName: 'Acme Storefront', severity: 'critical', status: 'open', createdAt: NOW_ISO, ageDays: 6 },
  { id: 'ds-att-2', displayId: 'OV2-102', title: 'Focus ring missing on primary button', projectName: 'Marketing Site', severity: 'high', status: 'in-progress', createdAt: NOW_ISO, ageDays: 15 },
  { id: 'ds-att-3', displayId: 'OV2-096', title: 'Heading skips a level', projectName: 'Acme Storefront', severity: 'medium', status: 'open', createdAt: NOW_ISO, ageDays: 21 },
];
